// Native subscriptions via the installed RevenueCat Capacitor SDK.
// The backend remains the entitlement authority after every purchase/restore.
import { Purchases } from "@revenuecat/purchases-capacitor";
import { api } from "@/lib/api";

const IOS_KEY = process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
const ANDROID_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
let configuredUserId = null;
let identityQueue = Promise.resolve();

// Native identity changes cannot be cancelled once sent to the SDK. Keep them
// in invocation order, including logout during an unfinished first configure.
// The caller still receives failures; one rejection must not poison the queue.
function serializeIdentity(operation) {
  const result = identityQueue.then(operation);
  identityQueue = result.catch(() => {});
  return result;
}

const isNative = () => {
  if (typeof window === "undefined") return false;
  return !!window.Capacitor?.isNativePlatform?.();
};

export const platform = () => {
  if (!isNative()) return "web";
  return window.Capacitor?.getPlatform?.() || "web";
};

function apiKey() {
  return platform() === "ios" ? IOS_KEY : platform() === "android" ? ANDROID_KEY : "";
}

function validProductionKey() {
  const key = apiKey();
  if (platform() === "ios") return key.startsWith("appl_");
  return !!key;
}

export const configureIAP = (userId) => serializeIdentity(async () => {
  // Check cached identity when this operation runs, not before queued switches.
  if (!isNative() || !userId || configuredUserId === userId) return false;
  const key = apiKey();
  if (!validProductionKey()) {
    console.error("Native purchases are disabled because this build does not contain a production RevenueCat key.");
    return false;
  }
  const status = await Purchases.isConfigured();
  if (!status?.isConfigured) {
    await Purchases.configure({ apiKey: key, appUserID: userId });
  } else {
    await Purchases.logIn({ appUserID: userId });
  }
  configuredUserId = userId;
  return true;
});

export const disconnectIAP = () => serializeIdentity(async () => {
  if (!isNative() || !configuredUserId) return;
  await Purchases.logOut();
  // A failed logout has not proved disconnection and must remain retryable.
  configuredUserId = null;
});

export const iapAvailable = () => isNative() && validProductionKey();

async function storeProduct(productId) {
  const { products } = await Purchases.getProducts({
    productIdentifiers: [productId],
  });
  const product = products?.find((candidate) => candidate.identifier === productId) || products?.[0];
  if (!product) throw new Error("Subscription product is not available in this store.");
  return product;
}

async function syncEntitlement(productId) {
  const response = await api.post("/iap/revenuecat-sync", { product_id: productId });
  return response.data;
}

export const purchase = async (productId) => {
  if (!iapAvailable()) throw new Error("Native purchases are not configured for this build.");
  const product = await storeProduct(productId);
  await Purchases.purchaseStoreProduct({ product });

  try {
    return await syncEntitlement(productId);
  } catch (error) {
    // The store purchase is already confirmed. RevenueCat's webhook or Restore
    // Purchases can safely complete server-authoritative activation later.
    console.warn("Subscription purchased; entitlement sync pending", error);
    return {
      active: false,
      pending: true,
      product_id: productId,
    };
  }
};

export const restorePurchases = async () => {
  if (!iapAvailable()) throw new Error("Native purchases are not configured for this build.");
  await Purchases.restorePurchases();
  return syncEntitlement(null);
};
